/* eslint-disable no-restricted-syntax */
/* eslint-disable no-console */
/**
 * @fileoverview Hook de Form Layer para gestión de estado de formulario del Template Carousel
 * @module useTemplateCarouselForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import { useState, useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

/**
 * Hook de Form Layer para gestión de Template Carousel.
 */
const useTemplateCarouselForm = (carouselData = {}, formContext) => {
  const { setValue, getValues, control } = formContext;

  // Estado interno para tracking de archivos con metadata
  const [filesState, setFilesState] = useState({});
  const [formDirty, setFormDirty] = useState(false);

  /**
   * Observa archivos usando react-hook-form para sincronización.
   */
  const watchedFiles = useWatch({
    control,
    name: carouselData?.cards?.map((_, index) => `carouselFile_${index}`) || [],
    defaultValue: []
  });

  /**
   * Actualiza archivo para un carousel específico con metadata básica.
   *
   * Función optimizada que actualiza el archivo en react-hook-form y
   * mantiene metadata básica en estado interno para referencia rápida.
   */
  const setFile = useCallback((carouselIndex, file, additionalMetadata = {}) => {
    if (typeof carouselIndex !== 'number' || carouselIndex < 0) {
      console.warn('setFile: carouselIndex must be a valid positive number');
      return;
    }

    if (!file || !(file instanceof File)) {
      console.warn('setFile: file must be a valid File object');
      return;
    }

    // Extraer metadata básica del archivo
    const fileMetadata = {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      ...additionalMetadata
    };

    const fieldName = `carouselFile_${carouselIndex}`;

    // Actualizar react-hook-form
    setValue(fieldName, [file], { shouldValidate: false });

    // Actualizar estado interno con metadata
    setFilesState((prev) => {
      const newState = {
        ...prev,
        [carouselIndex]: {
          file,
          metadata: fileMetadata,
          isValid: true, // Asumimos válido hasta que Actions Layer valide
          error: null
        }
      };
      return newState;
    });

    // Marcar como dirty
    setFormDirty(true);
  }, [setValue]);

  /**
   * Remueve archivo de un carousel específico.
   *
   * Limpia completamente el archivo del carousel especificado,
   * tanto del estado de react-hook-form como del estado interno.
   */
  const removeFile = useCallback((carouselIndex) => {
    if (typeof carouselIndex !== 'number' || carouselIndex < 0) {
      console.warn('removeFile: carouselIndex must be a valid positive number');
      return;
    }

    // Limpiar react-hook-form
    setValue(`carouselFile_${carouselIndex}`, [], { shouldValidate: false });

    // Limpiar estado interno
    setFilesState((prev) => {
      const newState = { ...prev };
      delete newState[carouselIndex];
      return newState;
    });

    // Marcar como dirty
    setFormDirty(true);
  }, [setValue]);

  /**
   * Actualiza valor de una variable específica del template.
   *
   * Función optimizada para actualizar variables dinámicas del template
   * manteniendo sincronización con react-hook-form.
   */
  const updateVariable = useCallback((fieldName, value) => {
    if (!fieldName || typeof fieldName !== 'string') {
      console.warn('updateVariable: fieldName must be a valid string');
      return;
    }

    setValue(fieldName, value);
    setFormDirty(true);
  }, [setValue]);

  /**
   * Valores memoizados de variables actuales del formulario.
   *
   * Proporciona acceso optimizado a todos los valores de variables
   * sin causar re-renders innecesarios.
   */
  const variables = useMemo(() => {
    const allValues = getValues();
    const variableValues = {};

    carouselData?.cards?.forEach((carousel) => {
      carousel?.body?.variables?.forEach((variable) => {
        variableValues[variable.fieldName] = allValues[variable.fieldName] || '';
      });
    });

    return variableValues;
  }, [carouselData?.cards, getValues]);

  /**
   * Estado de archivos memoizado con información completa.
   *
   * Combina información de react-hook-form con metadata del estado interno
   * para proporcionar vista completa del estado de archivos.
   */
  const files = useMemo(() => {
    const fileState = {};

    watchedFiles.forEach((fileArray, index) => {
      if (fileArray?.length > 0) {
        const file = fileArray[0];
        const internalState = filesState[index] || {};

        fileState[index] = {
          file,
          metadata: internalState.metadata || {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          },
          isValid: internalState.isValid !== false, // Default true
          error: internalState.error || null
        };
      }
    });

    return fileState;
  }, [watchedFiles, filesState]);

  /**
   * Validación memoizada del estado completo del formulario.
   *
   * Determina si el formulario está completo y válido basado en
   * variables requeridas y archivos necesarios.
   */
  const isFormValid = useMemo(() => {
    // Verificar que cada carousel tenga archivo
    for (let i = 0; i < (carouselData?.cards?.length || 0); i += 1) {
      if (!files[i]?.file) {
        return false;
      }
    }

    // Verificar que todas las variables requeridas estén llenas
    for (const carousel of carouselData?.cards || []) {
      for (const variable of carousel?.body?.variables || []) {
        const value = variables[variable.fieldName];
        if (!value || value.trim() === '') {
          return false;
        }
      }
    }

    return true;
  }, [carouselData?.cards, files, variables]);

  /**
   * Resetea completamente el estado del formulario.
   *
   * Limpia todos los archivos, variables y estado de validación,
   * devolviendo el formulario a su estado inicial.
   */
  const resetForm = useCallback(() => {
    // Limpiar archivos en react-hook-form
    carouselData?.cards?.forEach((_, index) => {
      setValue(`carouselFile_${index}`, [], { shouldValidate: false });
    });

    // Limpiar variables
    carouselData?.cards?.forEach((carousel) => {
      carousel?.body?.variables?.forEach((variable) => {
        setValue(variable.fieldName, '', { shouldValidate: false });
      });
    });

    // Resetear estado interno
    setFilesState({});
    setFormDirty(false);
  }, [carouselData?.cards, setValue]);

  /**
   * Objeto de retorno memoizado para prevenir re-renders innecesarios.
   *
   * @returns {Object} API completa del hook optimizada para performance
   */
  return useMemo(() => ({
    files,
    setFile,
    removeFile,
    variables,
    updateVariable,
    formErrors: {}, // TODO: Implementar errores específicos del form
    isFormValid,
    isDirty: formDirty,
    resetForm
  }), [
    files,
    setFile,
    removeFile,
    variables,
    updateVariable,
    isFormValid,
    formDirty,
    resetForm
  ]);
};

export default useTemplateCarouselForm;
